import type { AdminMetricsWindow } from '@carcheck/shared';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminMetrics, useAdminQueueCounts, useAdminViewStats } from '../../api';
import {
  AppealsIcon,
  CardsIcon,
  InspectorsIcon,
  PayoutsIcon,
  ReportsIcon,
} from '../../components/shell/icons';
import { tokens } from '../../design';
import { Routes } from '../../routes';
import { AnalyticsSection } from './_components/AnalyticsSection';
import { ComingSoonTile } from './_components/ComingSoonTile';
import { QueueCountTile, type QueueTileTone } from './_components/QueueCountTile';
import { RangeSelector } from './_components/RangeSelector';
import { SystemHealthTile } from './_components/SystemHealthTile';
import { ViewsSection } from './_components/ViewsSection';
import { formatRelative } from '../../utils/format';

/**
 * Dashboard — the universal admin landing (visible to ALL admins; NOT
 * gated behind `view_analytics`). Mirrors `design/mockups/admin/dashboard.html`.
 *
 * WIRED to real backends:
 *   - Queue-count tiles ← `GET /admin/queue-counts` (pendingReports,
 *     pendingAppeals, pendingKyc, failedPayouts, suspendedCards).
 *   - System-health tile ← `GET /health` (db / redis / s3 + uptime + status).
 *   - Analytics tiles ← `GET /admin/metrics?window=<w>` (GMV series,
 *     order-structure donut, online-now, orders-by-hour). ONE fetch per
 *     window, shared by all four tiles; the top-bar range selector drives
 *     the `window` state. These four are gated behind `view_analytics`
 *     (per-tile `<RequirePermission>` → locked placeholder fallback so the
 *     grid stays intact); the page itself is the universal landing and is
 *     NOT gated.
 *
 * The queue-counts + health fetches are shared with the sidebar badges
 * (single fetch, two consumers — TanStack dedupes by key).
 *
 * NOT wired (no backend yet) — rendered as subtle "Скоро" skeletons, NO
 * hardcoded mock numbers: appeal-queue load heatmap, admin-activity feed.
 * (The heatmap was specced as report-moderation latency; AUT-265 retired
 * report moderation, so appeals are the only queue left to measure.)
 */

const LivePill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 9px',
  background: tokens.state.successBg,
  color: tokens.state.success,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
});

const LiveDot = styled('span')({
  width: 6,
  height: 6,
  borderRadius: 999,
  background: tokens.state.success,
  boxShadow: '0 0 0 3px rgba(22, 163, 106, 0.2)',
});

/** Which `AdminQueueCountsDto` field each count tile reads. */
type CountField =
  | 'pendingReports'
  | 'pendingAppeals'
  | 'pendingKyc'
  | 'failedPayouts'
  | 'suspendedCards';

interface CountTileSpec {
  field: CountField;
  labelKey: string;
  icon: ReactNode;
  to: string;
  tone?: QueueTileTone;
  /** i18n key for the hover description of the count's predicate. */
  hintKey?: string;
}

/** Static tile config — field → label / icon / route / tone (mockup order). */
const COUNT_TILES: CountTileSpec[] = [
  // AUT-265 — `pendingReports` no longer counts reports awaiting a
  // moderator (there is no moderator). The backend repointed it at STUCK
  // reports: final, unaccepted, finalized over 24h ago — meaning the
  // auto-accept job died or an appeal is holding it. Label + hint say so.
  {
    field: 'pendingReports',
    labelKey: 'dashboard.tiles.pendingReports',
    hintKey: 'dashboard.tiles.pendingReportsHint',
    icon: <ReportsIcon />,
    to: Routes.App.Reports,
  },
  {
    field: 'pendingAppeals',
    labelKey: 'dashboard.tiles.pendingAppeals',
    icon: <AppealsIcon />,
    to: Routes.App.Appeals,
  },
  {
    field: 'pendingKyc',
    labelKey: 'dashboard.tiles.pendingKyc',
    icon: <InspectorsIcon />,
    to: Routes.App.Inspectors,
  },
  {
    field: 'failedPayouts',
    labelKey: 'dashboard.tiles.failedPayouts',
    icon: <PayoutsIcon />,
    to: Routes.App.Payouts,
    tone: 'danger',
  },
  {
    field: 'suspendedCards',
    labelKey: 'dashboard.tiles.suspendedCards',
    icon: <CardsIcon />,
    to: Routes.App.Cards,
    tone: 'danger',
  },
];

interface DashboardHeaderProps {
  computedAt: string | undefined;
  window: AdminMetricsWindow;
  onWindowChange: (window: AdminMetricsWindow) => void;
}

const DashboardHeader = ({
  computedAt,
  window,
  onWindowChange,
}: DashboardHeaderProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography component="h1" variant="h1">
          {t('nav.dashboard')}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.text.tertiary, mt: 0.5 }}>
          {computedAt
            ? t('dashboard.updated', { time: formatRelative(computedAt) })
            : t('dashboard.updatedNever')}
        </Typography>
      </Box>
      <RangeSelector value={window} onChange={onWindowChange} />
      <LivePill>
        <LiveDot />
        {t('dashboard.live')}
      </LivePill>
    </Box>
  );
};

export const DashboardPage = (): ReactElement => {
  const { t } = useTranslation();
  const [window, setWindow] = useState<AdminMetricsWindow>('7d');
  const { data, isLoading, isError } = useAdminQueueCounts();
  const {
    data: metrics,
    isLoading: metricsLoading,
    isError: metricsError,
  } = useAdminMetrics(window);
  const {
    data: viewStats,
    isLoading: viewStatsLoading,
    isError: viewStatsError,
  } = useAdminViewStats(window);

  return (
    <Box>
      <DashboardHeader computedAt={data?.computedAt} window={window} onWindowChange={setWindow} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gridAutoRows: 'minmax(120px, auto)',
          gap: 1.5,
        }}
      >
        {COUNT_TILES.map((spec) => (
          <QueueCountTile
            key={spec.field}
            label={t(spec.labelKey)}
            count={data?.[spec.field]}
            icon={spec.icon}
            to={spec.to}
            tone={spec.tone}
            hint={spec.hintKey ? t(spec.hintKey) : undefined}
            isLoading={isLoading}
            isError={isError}
          />
        ))}

        <SystemHealthTile gridColumn="span 6" />

        <AnalyticsSection metrics={metrics} isLoading={metricsLoading} isError={metricsError} />

        <ViewsSection stats={viewStats} isLoading={viewStatsLoading} isError={viewStatsError} />

        <ComingSoonTile label={t('dashboard.comingSoon.heatmap')} gridColumn="span 6" />
        <ComingSoonTile label={t('dashboard.comingSoon.activity')} gridColumn="span 6" />
      </Box>
    </Box>
  );
};
